import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Button,
  Badge,
  InputGroup,
  Modal,
  Alert,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import { Copy, Check, Mail, Send, X, RefreshCw, Trash, Plus, Home as Building, Download, Clock, Shield, Infinity, Save, Folder, Edit2 } from "react-feather";
import { QRCodeCanvas } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../ThemeContext";
import { apifetch } from '../services/interceptors';
import { getSnapshotName } from '../utils/snapshotBranding';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const statusMap = {
  'just created': { text: "Active", color: "success" },
  active: { text: "Active", color: "success" },
  downloaded: { text: "Downloaded", color: "info" },
  expired: { text: "Expired", color: "warning" },
  revoked: { text: "Revoked", color: "danger" },
};

// Default email template
const DEFAULT_EMAIL_TEMPLATE = {
  id: 'default',
  name: 'Default Template',
  subject: '{snapshotName} for {orgName}',
  body: "The {snapshotName} your company has requested has begun.\n\n" +
    "To continue, please follow the steps below using the secure website provided. " +
    "This website was created specifically for your organization and should not be shared with anyone outside of it:\n\n" +
    "Secure Website:\n" +
    "{downloadLink}\n\n" +
    "Steps to Continue:\n" +
    "1. Open the secure website listed above in your browser.\n" +
    "2. Confirm your organization's name, then click \"Download Snapshot.\"\n" +
    "3. A file will automatically download to your Downloads folder.\n" +
    "4. Open your Downloads folder and double-click the file to start the test.\n" +
    "5. A window will pop up asking you to \"START SCANNING\", click on start scanning.\n" +
    "6. The window will then minimize, and exit when complete.\n" +
    "7. You can use your computer as normal while the scan is running.\n\n" +
    "About the {snapshotName}:\n" +
    "• It typically runs for 15 minutes to 1 hour.\n" +
    "• You can continue using your computer during this time.\n" +
    "• Once the test finishes, the window will close on its own.\n\n" +
    "Important Reminders:\n" +
    "• The analysis is completely safe. Nothing is copied, moved, or deleted from your device, network, or cloud accounts.\n" +
    "• Please have 3 to 5 other users in your organization run this test on their computers.\n" +
    "• Try to complete the analysis at least 72 hours before our next meeting.\n\n" +
    "Download Details:\n" +
    "{downloadInfo}\n\n" +
    "If you have any questions, just let us know!"
};

export default function GenerateScannerLink({orgid}) {
  const { isDarkMode } = useTheme();
  const themeClass = isDarkMode ? "bg-dark text-light" : "bg-light text-dark";

  // Form state
  const [downloadLimit, setDownloadLimit] = useState(5);
  const [expiresIn, setExpiresIn] = useState(24);
  const [isUnlimitedDownloads, setIsUnlimitedDownloads] = useState(false);
  const [isNeverExpires, setIsNeverExpires] = useState(false);

  // Link state
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkStatus, setLinkStatus] = useState("just created");
  const [generatedToken, setGeneratedToken] = useState("");
  const [generatedFilename, setGeneratedFilename] = useState("");
  const [generatedFor, setGeneratedFor] = useState("");

  // Tenant and Organization info
  const [tenantInfo, setTenantInfo] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const token = localStorage.getItem("token");
  const [error, setError] = useState("");
  
  // Email state
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_TEMPLATE.body);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_TEMPLATE.subject);
  
  // Email Template Management
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [templateError, setTemplateError] = useState("");
  const [templateMessage, setTemplateMessage] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: "", body: "" });
  
  // Organization creation states
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [website, setWebsite] = useState("");
  const [primaryPerson, setPrimaryPerson] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [orgError, setOrgError] = useState("");
  const [orgMessage, setOrgMessage] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);

  // Token management
  const [generatedLinks, setGeneratedLinks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch email templates
  const fetchTemplates = async () => {
    try {
      const response = await apifetch("s2/api/email-templates", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  // Save/Update template
  const saveTemplate = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    setTemplateError("");
    setTemplateMessage("");

    if (!templateName.trim()) {
        setTemplateError("Template name is required");
        setSavingTemplate(false);
        return;
    }

    try {
        const url = templateId 
            ? `s2/api/email-templates/${templateId}` 
            : "s2/api/email-templates";
        const method = templateId ? "PUT" : "POST";

        const response = await apifetch(url, {
            method: method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                name: templateName,
                subject: templateSubject,
                body: templateBody,
                // No template_type specified, defaults to 'localhost' in database
            }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
            setTemplateMessage(data.message || "Template saved successfully");
            
            // UPDATE FORM STATE DIRECTLY WITH PAYLOAD
            setEmailSubject(templateSubject);
            setEmailBody(templateBody);
            
            // If updating existing template, keep it selected
            if (templateId) {
                setSelectedTemplate(templateId);
            }
            
            // Update the templates list locally (no extra API call)
            if (templateId) {
                // Update existing template in the list
                setTemplates(prev => prev.map(t => 
                    t.id === templateId 
                        ? { ...t, name: templateName, subject: templateSubject, body: templateBody }
                        : t
                ));
            } else {
                // Add new template to the list
                const newTemplate = {
                    id: data.template?.id || 'temp_' + Date.now(),
                    name: templateName,
                    subject: templateSubject,
                    body: templateBody,
                    is_default: false
                };
                setTemplates(prev => [...prev, newTemplate]);
                setSelectedTemplate(newTemplate.id);
            }

            setTimeout(() => {
                setShowTemplateModal(false);
                setTemplateMessage("");
                resetTemplateForm();
            }, 1500);
        } else {
            setTemplateError(data.error || "Failed to save template");
        }
    } catch (error) {
        setTemplateError(`Error saving template: ${error.message}`);
    } finally {
        setSavingTemplate(false);
    }
};

  // Delete template
  const deleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      const response = await apifetch(`s2/api/email-templates/${templateToDelete}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchTemplates();
        setShowDeleteConfirm(false);
        setTemplateToDelete(null);
      } else {
        setTemplateError(data.error || "Failed to delete template");
      }
    } catch (error) {
      setTemplateError(`Error deleting template: ${error.message}`);
    }
  };

  // Load template
  const loadTemplate = (template) => {
    setSelectedTemplate(template.id);
    setEmailSubject(template.subject);
    setEmailBody(template.body);
  };

  // Open template editor for new or existing
  const openTemplateEditor = (template = null) => {
    if (template) {
      setTemplateId(template.id);
      setTemplateName(template.name);
      setTemplateSubject(template.subject);
      setTemplateBody(template.body);
    } else {
      resetTemplateForm();
    }
    setTemplateError("");
    setTemplateMessage("");
    setShowTemplateModal(true);
  };

  const resetTemplateForm = () => {
    setTemplateId(null);
    setTemplateName("");
    setTemplateSubject("");
    setTemplateBody("");
    setTemplateError("");
    setTemplateMessage("");
  };

  // Fetch tenant info and organizations
  const fetchTenantAndOrganizations = async () => {
    if (orgid) {
      setSelectedOrganization(orgid);
      return;
    }
    try {
      const tenantResponse = await apifetch("s2/api/scanner/tenant-info", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (tenantResponse.ok) {
        const tenantData = await tenantResponse.json();
        setTenantInfo(tenantData);
      }

      const orgResponse = await apifetch("s1/api/organizations", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (orgResponse.ok) {
        const data = await orgResponse.json();
        setOrganizations(data.organizations || []);
        if (data.organizations && data.organizations.length > 0) {
          if (!orgid) {
            setSelectedOrganization(orgid);
          } else {
            setSelectedOrganization(data.organizations[0].id);
          }
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchExistingLinks = async () => {
    if (orgid) return;
    try {
      const response = await apifetch("/s2/api/scanner/tokens", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedLinks(data.tokens || []);
        setTotalCount(data.count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch links:", err);
    }
  };

  useEffect(() => {
    fetchTenantAndOrganizations();
    fetchExistingLinks();
    fetchTemplates();
  }, []);

  const createOrganization = async (e) => {
    e.preventDefault();
    setCreatingOrg(true);
    setOrgError("");
    setOrgMessage("");
    
    try {
      const response = await apifetch('s1/api/create_organization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          primaryPerson,
          primaryEmail,
          primaryPhone,
          website,
          name: organizationName
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setOrgError(data.error || "Can't create organization");
      } else {
        await fetchTenantAndOrganizations();
        if (data.organization && data.organization.id) {
          setSelectedOrganization(data.organization.id);
        }
        setOrgMessage('Organization created successfully');
        setOrganizationName('');
        setWebsite('');
        setPrimaryPerson('');
        setPrimaryEmail('');
        setPrimaryPhone('');
        setTimeout(() => {
          setShowOrgModal(false);
          setOrgMessage("");
        }, 1500);
      }
    } catch (error) {
      setOrgError(`Error creating organization: ${error.message}`);
    } finally {
      setCreatingOrg(false);
    }
  };

  const generateLink = async () => {
    if (!selectedOrganization) {
      setError("Please select an organization first.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const requestBody = {
        organization_id: orgid ? orgid : selectedOrganization,
        download_limit: isUnlimitedDownloads ? 999999 : downloadLimit,
        expires_in: isNeverExpires ? 87600 : expiresIn,
      };

      if (tenantInfo) {
        requestBody.tenant_name = tenantInfo.name || tenantInfo.tenant_name;
        requestBody.org_name = tenantInfo.org_name || organizations.find(o => o.id === selectedOrganization)?.name || '';
      }

      const response = await apifetch("/s2/api/scanner/generate-link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setLink(data.download_url);
        setGeneratedToken(data.token);
        setGeneratedFilename(data.filename);
        setGeneratedFor(data.org_name || "");
        setLinkStatus("just created");
        updatePreview(data.download_url, data.token, data.filename);
        await fetchExistingLinks();
      } else {
        setError(data.error || data.message || "Failed to generate link. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const revokeLink = async () => {
    if (!generatedToken) return;
    
    if (!window.confirm('Are you sure you want to revoke this link? It will no longer work for downloads.')) {
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await apifetch(`/s2/api/scanner/revoke/${generatedToken}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setLinkStatus("revoked");
        setError("");
        alert(data.message || 'Link revoked successfully');
        await fetchExistingLinks();
      } else {
        setError(data.error || "Failed to revoke link");
      }
    } catch (err) {
      console.error("Failed to revoke:", err);
      setError(err.message || "Network error while revoking");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    const fullUrl = `${window.location.origin}${link}`;
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const validateEmail = (email) => {
    if (!EMAIL_REGEX.test(email)) {
      return "Invalid email format";
    }
    return null;
  };

  const addEmail = () => {
    const emailList = emailInput
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(Boolean);

    if (emailList.length === 0) return;

    const newEmails = [];

    for (const email of emailList) {
      const validationError = validateEmail(email);
      if (validationError) {
        setEmailError(`${email}: ${validationError}`);
        return;
      }

      if (emails.includes(email) || newEmails.includes(email)) {
        setEmailError(`${email}: Email already added`);
        return;
      }

      newEmails.push(email);
    }

    setEmails([...emails, ...newEmails]);
    setEmailInput("");
    setEmailError("");
  };

  const handlePasteMultipleEmails = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    const emailList = pastedText.split(/[,;\n\r\t]+/).map(email => email.trim()).filter(email => email);
    
    const validEmails = [];
    const invalidEmails = [];
    
    emailList.forEach(email => {
      const trimmedEmail = email.trim();
      
      if (EMAIL_REGEX.test(trimmedEmail) && !emails.includes(trimmedEmail)) {
        validEmails.push(trimmedEmail);
      } else if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) {
        invalidEmails.push(trimmedEmail);
      }
    });
    
    if (validEmails.length > 0) {
      setEmails([...emails, ...validEmails]);
    }
    
    if (invalidEmails.length > 0) {
      setEmailError(`${invalidEmails.length} invalid email(s) found: ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`);
    } else {
      setEmailError("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const updatePreview = (scanLink, token, filename) => {
    const fullUrl = `${window.location.origin}${scanLink}`;
    const snapshotName = getSnapshotName(tenantInfo?.name || tenantInfo?.tenant_name);
    const tenantName = tenantInfo?.name || tenantInfo?.tenant_name || '[Tenant]';
    const orgName = organizations.find(o => o.id === selectedOrganization)?.name || '[Organization]';
    
    const downloadInfo = isUnlimitedDownloads && isNeverExpires 
      ? "• Unlimited downloads with no expiration" 
      : isUnlimitedDownloads 
        ? "• Unlimited downloads" 
        : isNeverExpires 
          ? "• No expiration date" 
          : `• ${downloadLimit} downloads • Expires in ${expiresIn} hours`;

    const subject = emailSubject
      .replace(/{snapshotName}/g, () => snapshotName)
      .replace(/{downloadLink}/g, fullUrl)
      .replace(/{downloadLimit}/g, isUnlimitedDownloads ? 'Unlimited' : downloadLimit)
      .replace(/{expiresIn}/g, isNeverExpires ? 'Never' : expiresIn)
      .replace(/{tenantName}/g, tenantName)
      .replace(/{orgName}/g, orgName)
      .replace(/{downloadInfo}/g, downloadInfo);

    const body = emailBody
      .replace(/{snapshotName}/g, () => snapshotName)
      .replace(/{downloadLink}/g, fullUrl)
      .replace(/{downloadLimit}/g, isUnlimitedDownloads ? 'Unlimited' : downloadLimit)
      .replace(/{expiresIn}/g, isNeverExpires ? 'Never' : expiresIn)
      .replace(/{tenantName}/g, tenantName)
      .replace(/{orgName}/g, orgName)
      .replace(/{downloadInfo}/g, downloadInfo);
    
    setPreviewContent({ subject, body });
  };

  useEffect(() => {
    if (link) {
      updatePreview(link, generatedToken, generatedFilename);
    }
  }, [emailBody, emailSubject, downloadLimit, expiresIn, link, generatedToken, generatedFilename, isUnlimitedDownloads, isNeverExpires, selectedOrganization, tenantInfo]);

  const getEmailContent = () => {
    const fullUrl = `${window.location.origin}${link}`;
    const snapshotName = getSnapshotName(tenantInfo?.name || tenantInfo?.tenant_name);
    const tenantName = tenantInfo?.name || tenantInfo?.tenant_name || '[Tenant]';
    const orgName = organizations.find(o => o.id === selectedOrganization)?.name || '[Organization]';
    
    const downloadInfo = isUnlimitedDownloads && isNeverExpires 
      ? "• Unlimited downloads with no expiration" 
      : isUnlimitedDownloads 
        ? "• Unlimited downloads" 
        : isNeverExpires 
          ? "• No expiration date" 
          : `• ${downloadLimit} downloads • Expires in ${expiresIn} hours`;

    const subject = emailSubject
      .replace(/{snapshotName}/g, () => snapshotName)
      .replace(/{downloadLink}/g, fullUrl)
      .replace(/{downloadLimit}/g, isUnlimitedDownloads ? 'Unlimited' : downloadLimit)
      .replace(/{expiresIn}/g, isNeverExpires ? 'Never' : expiresIn)
      .replace(/{tenantName}/g, tenantName)
      .replace(/{orgName}/g, orgName)
      .replace(/{downloadInfo}/g, downloadInfo);

    const body = emailBody
      .replace(/{snapshotName}/g, () => snapshotName)
      .replace(/{downloadLink}/g, fullUrl)
      .replace(/{downloadLimit}/g, isUnlimitedDownloads ? 'Unlimited' : downloadLimit)
      .replace(/{expiresIn}/g, isNeverExpires ? 'Never' : expiresIn)
      .replace(/{tenantName}/g, tenantName)
      .replace(/{orgName}/g, orgName)
      .replace(/{downloadInfo}/g, downloadInfo);

    return { subject, body };
  };

  const getMailToUrl = (provider) => {
    const emailList = emails.join(',');
    const { subject, body } = getEmailContent();
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    
    switch(provider) {
      case 'gmail':
        return `https://mail.google.com/mail/?view=cm&fs=1&to=${emailList}&su=${encodedSubject}&body=${encodedBody}`;
      case 'outlook':
        return `https://outlook.live.com/mail/0/deeplink/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      case 'outlook-office':
        return `https://outlook.office.com/mail/deeplink/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      case 'yahoo':
        return `https://mail.yahoo.com/mail/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      default:
        return `mailto:${emailList}?subject=${encodedSubject}&body=${encodedBody}`;
    }
  };

  const openEmailClient = (provider) => {
    if (emails.length === 0) {
      setEmailError("Please add at least one recipient email address.");
      return;
    }
    
    const url = getMailToUrl(provider);
    const emailWindow = window.open(url, "_blank");
    
    if (provider === 'outlook' || provider === 'outlook-office') {
      setTimeout(() => {
        if (emailWindow && emailWindow.closed === false) return;
        const { subject, body } = getEmailContent();
        window.location.href = `mailto:${emails.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }, 2000);
    }
  };

  const resetOrgForm = () => {
    setOrganizationName("");
    setWebsite("");
    setPrimaryPerson("");
    setPrimaryEmail("");
    setPrimaryPhone("");
    setOrgError("");
    setOrgMessage("");
  };

  const getStatusBadge = (link) => {
    const isExpired = new Date(link.expires_at) < new Date();
    if (link.is_revoked) {
      return <Badge bg="danger">Revoked</Badge>;
    }
    if (isExpired) {
      return <Badge bg="warning">Expired</Badge>;
    }
    if (link.download_count > 0) {
      return <Badge bg="info">Downloaded ({link.download_count}/{link.download_limit === 999999 ? '∞' : link.download_limit})</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  return (
    <>
      <Card className={`shadow-lg border-0 ${themeClass} mb-4`}>
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-4">
            <Download className="me-2" size={20} />
            Generate Scanner Download Link
          </h5>

          {/* Display Tenant Info */}
          {tenantInfo && (
            <div className="mb-3 p-3 bg-primary bg-opacity-10 rounded">
              <div className="d-flex align-items-center gap-3">
                <Shield size={20} className="text-primary" />
                <div>
                  <span className="fw-bold">Tenant:</span> {tenantInfo.name || tenantInfo.tenant_name}
                  {tenantInfo.org_name && (
                    <>
                      <span className="mx-2">|</span>
                      <span className="fw-bold">Organization:</span> {tenantInfo.org_name}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {!orgid && (<Form.Group controlId="organizationSelect" className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label style={{color: isDarkMode ? "white" : "black", margin: 0}}>
                Select Organization
              </Form.Label>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => {
                  resetOrgForm();
                  setShowOrgModal(true);
                }}
              >
                <Building size={14} className="me-1" />
                New Organization
              </Button>
            </div>
            <Form.Select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              required
              className={isDarkMode ? 'bg-dark text-light' : ''}
            > 
              {organizations.length === 0 && (
                <option value="" disabled>No organizations available. Click "New Organization" to create one.</option>
              )}
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
            {organizations.length === 0 && (
              <Form.Text className="text-warning">
                You need to create an organization before generating a scanner link.
              </Form.Text>
            )}
          </Form.Group>)}

          {/* Download Limit with Unlimited Option */}
          <div className="mb-3">
            <Form.Label style={{color: isDarkMode ? "white" : "black"}}>
              Download Limit
            </Form.Label>
            <div className="d-flex gap-3 align-items-center">
              <Form.Check
                type="checkbox"
                id="unlimited-downloads"
                label="Unlimited downloads"
                checked={isUnlimitedDownloads}
                onChange={(e) => {
                  setIsUnlimitedDownloads(e.target.checked);
                  if (e.target.checked) {
                    setDownloadLimit(999999);
                  } else {
                    setDownloadLimit(5);
                  }
                }}
                className={isDarkMode ? "text-light" : ""}
              />
              {!isUnlimitedDownloads && (
                <Form.Control
                  type="number"
                  className={`w-50 ${isDarkMode ? "bg-dark text-light" : ""}`}
                  value={downloadLimit}
                  onChange={(e) => setDownloadLimit(parseInt(e.target.value) || 1)}
                  min={1}
                  max={1000}
                  disabled={isUnlimitedDownloads}
                />
              )}
            </div>
          </div>

          {/* Expiry with Never Expires Option */}
          <div className="mb-3">
            <Form.Label style={{color: isDarkMode ? "white" : "black"}}>
              Expires In
            </Form.Label>
            <div className="d-flex gap-3 align-items-center">
              <Form.Check
                type="checkbox"
                id="never-expires"
                label="Never expires"
                checked={isNeverExpires}
                onChange={(e) => {
                  setIsNeverExpires(e.target.checked);
                  if (e.target.checked) {
                    setExpiresIn(87600);
                  } else {
                    setExpiresIn(24);
                  }
                }}
                className={isDarkMode ? "text-light" : ""}
              />
              {!isNeverExpires && (
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="number"
                    className={`w-50 ${isDarkMode ? "bg-dark text-light" : ""}`}
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(parseInt(e.target.value) || 1)}
                    min={1}
                    max={8760}
                    disabled={isNeverExpires}
                  />
                  <span className={isDarkMode ? "text-light" : ""}>hours</span>
                </div>
              )}
            </div>
          </div>

          <Button
            className="w-100 mb-3"
            disabled={
              loading ||
              (!orgid &&
                (
                  selectedOrganization === "" ||
                  organizations.length === 0
                ))
            }
            onClick={generateLink}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Generating…
              </>
            ) : (
              "Generate Download Link"
            )}
          </Button>

          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          )}

          <AnimatePresence>
            {link && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <hr className="my-4" />

                <div className="d-flex align-items-center gap-2 mb-2">
                  {getStatusBadge({ 
                    is_revoked: linkStatus === "revoked",
                    expires_at: isNeverExpires ? new Date(Date.now() + 87600 * 3600000).toISOString() : new Date(Date.now() + expiresIn * 3600000).toISOString(),
                    download_count: 0,
                    download_limit: isUnlimitedDownloads ? 999999 : downloadLimit
                  })}
                  <span className="small">
                    Token: {generatedToken?.substring(0, 16)}...
                  </span>
                </div>

                <InputGroup className={`${isDarkMode ? "bg-dark" : ""} mb-3`}>
                  <Form.Control 
                    className={`${isDarkMode ? "bg-dark text-light" : ""}`} 
                    readOnly 
                    value={`${window.location.origin}${link}`} 
                  />
                  <Button onClick={copyLink}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                  <Button variant="outline-danger" onClick={revokeLink} disabled={linkStatus === "revoked"}>
                    <Trash size={16} />
                  </Button>
                </InputGroup>

                <div className="text-center mb-3">
                  <QRCodeCanvas value={`${window.location.origin}${link}`} size={120} />
                </div>

                <div className={`${isDarkMode ? "bg-dark text-light" : ""} p-3 rounded mb-3`}>
                  <small className="">
                    <strong>Download Details:</strong><br />
                    • Filename: {generatedFilename || 'scanner.exe'}<br />
                    • Organization: {generatedFor}<br />
                    • Download Limit: {isUnlimitedDownloads ? 'Unlimited' : downloadLimit} times<br />
                    • Expires: {isNeverExpires ? 'Never' : `${expiresIn} hours`}
                  </small>
                </div>

                {/* Email Templates Section */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className={`${isDarkMode ? "text-light" : "text-muted"} text-uppercase mb-0`}>
                      <Mail size={16} className="me-2" />
                      Email Templates
                    </h6>
                    <div className="d-flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={() => openTemplateEditor()}
                      >
                        <Plus size={14} className="me-1" />
                        New Template
                      </Button>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          <Folder size={14} className="me-1" />
                          {selectedTemplate ? 'Template' : 'Load Template'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={isDarkMode ? "bg-dark" : ""}>
                          <Dropdown.Item 
                            onClick={() => {
                              setSelectedTemplate(null);
                              setEmailSubject(DEFAULT_EMAIL_TEMPLATE.subject);
                              setEmailBody(DEFAULT_EMAIL_TEMPLATE.body);
                            }}
                            className={isDarkMode ? "text-light" : ""}
                          >
                            Default Template
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {templates.map((template) => (
                            <Dropdown.Item 
                              key={template.id}
                              onClick={() => loadTemplate(template)}
                              className={`${isDarkMode ? "text-light" : ""} d-flex justify-content-between align-items-center`}
                            >
                              <span>{template.name}</span>
                              <div className="d-flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTemplateEditor(template);
                                  }}
                                >
                                  <Edit2 size={12} />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTemplateToDelete(template.id);
                                    setShowDeleteConfirm(true);
                                  }}
                                >
                                  <Trash size={12} />
                                </Button>
                              </div>
                            </Dropdown.Item>
                          ))}
                          {templates.length === 0 && (
                            <Dropdown.Item disabled>No saved templates</Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                  {selectedTemplate && (
                    <div className="small text-muted mb-2">
                      <Badge bg="info" className="me-1">Loaded</Badge>
                      {templates.find(t => t.id === selectedTemplate)?.name || 'Custom'}
                    </div>
                  )}
                </div>

                {/* Enhanced Email Input Section */}
                <h6 className={`${isDarkMode ? "text-light" : "text-muted"} text-uppercase mt-4`}>Add Recipients</h6>
                
                {/* Email chips display */}
                <div className="d-flex flex-wrap gap-2 p-2 border rounded mb-2" style={{ minHeight: '60px' }}>
                  {emails.map((e) => (
                    <Badge key={e} pill bg="primary" className="d-flex align-items-center gap-1 p-2">
                      <span>{e}</span>
                      <X size={12} style={{ cursor: 'pointer' }} onClick={() => removeEmail(e)} />
                    </Badge>
                  ))}
                  {emails.length === 0 && (
                    <span className="small">No emails added yet. Type or paste emails below.</span>
                  )}
                </div>

                {/* Email input with Add button */}
                <div className="mb-2">
                  <InputGroup>
                    <Form.Control
                      className={`${isDarkMode ? "bg-dark text-grey" : ""}`}
                      placeholder="Enter email address (e.g., user@example.com)"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError("");
                      }}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePasteMultipleEmails}
                      isInvalid={!!emailError}
                    />
                    <Button onClick={addEmail} variant="outline-primary">
                      <Plus size={16} /> Add
                    </Button>
                  </InputGroup>
                  {emailError && (
                    <Form.Text className="text-danger">
                      {emailError}
                    </Form.Text>
                  )}
                  <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"}`}>
                    Tip: You can paste multiple emails separated by commas, spaces, or new lines
                  </Form.Text>
                </div>

                {/* Email subject and body editor */}
                <h6 className={`${isDarkMode ? "text-light" : "text-muted"} text-uppercase mt-3`}>Email Content</h6>
                <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                  Use {'{downloadLink}'}, {'{downloadLimit}'}, {'{expiresIn}'}, {'{tenantName}'}, {'{snapshotName}'}, {'{orgName}'}, {'{downloadInfo}'} as placeholders
                </Form.Text>
                
                <Form.Group className="mb-2">
                  <Form.Label className={isDarkMode ? "text-light" : ""}>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className={`${isDarkMode ? "bg-dark text-light" : ""}`}
                    placeholder="Email subject line..."
                  />
                </Form.Group>

                <Form.Control
                  as="textarea"
                  rows={4}
                  value={emailBody} 
                  onChange={(e) => setEmailBody(e.target.value)}
                  className={`${isDarkMode ? "bg-dark text-light" : ""} mb-2`}
                  placeholder="Customize your email message..."
                />

                {/* Action buttons */}
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  <Button
                    variant="primary"
                    onClick={() => setShowPreview(true)}
                    disabled={!emails.length}
                  >
                    Preview Email
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => openEmailClient('gmail')}
                    disabled={!emails.length}
                  >
                    <Mail size={14} /> Gmail
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => openEmailClient('outlook')}
                    disabled={!emails.length}
                  >
                    <Send size={14} /> Outlook.com
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={() => openEmailClient('outlook-office')}
                    disabled={!emails.length}
                  >
                    <Send size={14} /> Microsoft 365
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => openEmailClient('yahoo')}
                    disabled={!emails.length}
                  >
                    <Mail size={14} /> Yahoo
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={() => {
                      if (selectedTemplate) {
                        const template = templates.find(t => t.id === selectedTemplate);
                        if (template) {
                          openTemplateEditor(template);
                        }
                      } else {
                        openTemplateEditor();
                      }
                    }}
                  >
                    <Save size={14} className="me-1" />
                    {selectedTemplate ? 'Update Template' : 'Save as Template'}
                  </Button>
                </div>

                <div className={` ${isDarkMode ? "text-light" : "text-muted"} small`}>
                  <strong>Supported providers:</strong> Gmail, Outlook.com, Hotmail, Live, Microsoft 365, Yahoo, and any custom domain (Exchange, etc.)
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card.Body>

        {/* Organization Creation Modal */}
        <Modal 
          show={showOrgModal} 
          onHide={() => {
            setShowOrgModal(false);
            resetOrgForm();
          }} 
          centered 
          size="lg"
          data-bs-theme={isDarkMode ? "dark" : "light"}
          contentClassName={isDarkMode ? "bg-dark" : ""}
          className={`organization-modal ${isDarkMode ? "bg-dark" : "light"}`}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>
              <Building className="me-2" size={20} />
              Create New Organization
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={createOrganization}>
            <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
              {orgError && (
                <Alert variant="danger" onClose={() => setOrgError("")} dismissible>
                  {orgError}
                </Alert>
              )}
              {orgMessage && (
                <Alert variant="success" onClose={() => setOrgMessage("")} dismissible>
                  {orgMessage}
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Organization Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="e.g., Acme Inc."
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Contact Person *</Form.Label>
                <Form.Control
                  type="text"
                  value={primaryPerson}
                  onChange={(e) => setPrimaryPerson(e.target.value)}
                  placeholder="Full name"
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Email *</Form.Label>
                <Form.Control
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                  placeholder="contact@example.com"
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={primaryPhone}
                  onChange={(e) => setPrimaryPhone(e.target.value)}
                  placeholder="+1 234 567 8900"
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
              <Button variant="secondary" onClick={() => {
                setShowOrgModal(false);
                resetOrgForm();
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={creatingOrg || !organizationName || !primaryPerson || !primaryEmail}
              >
                {creatingOrg ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  "Create Organization"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Email Template Modal */}
        <Modal 
          show={showTemplateModal} 
          onHide={() => {
            setShowTemplateModal(false);
            resetTemplateForm();
          }} 
          centered 
          size="lg"
          data-bs-theme={isDarkMode ? "dark" : "light"}
          contentClassName={isDarkMode ? "bg-dark" : ""}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>
              <Save className="me-2" size={20} />
              {templateId ? 'Update' : 'Save'} Email Template
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={saveTemplate}>
            <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
              {templateError && (
                <Alert variant="danger" onClose={() => setTemplateError("")} dismissible>
                  {templateError}
                </Alert>
              )}
              {templateMessage && (
                <Alert variant="success" onClose={() => setTemplateMessage("")} dismissible>
                  {templateMessage}
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Template Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Standard Scanner Email"
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  type="text"
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder="Email subject line..."
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Body Content</Form.Label>
                <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                  Use {'{downloadLink}'}, {'{downloadLimit}'}, {'{expiresIn}'}, {'{tenantName}'}, {'{snapshotName}'}, {'{orgName}'}, {'{downloadInfo}'} as placeholders
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={templateBody}
                  onChange={(e) => setTemplateBody(e.target.value)}
                  placeholder="Write your email template here..."
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
              <Button variant="secondary" onClick={() => {
                setShowTemplateModal(false);
                resetTemplateForm();
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={savingTemplate || !templateName.trim()}
              >
                {savingTemplate ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  templateId ? 'Update Template' : 'Save Template'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteConfirm}
          onHide={() => {
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
          }}
          centered
          data-bs-theme={isDarkMode ? "dark" : "light"}
          contentClassName={isDarkMode ? "bg-dark" : ""}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>
              Delete Template
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
            <p>Are you sure you want to delete this template? This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
            <Button variant="secondary" onClick={() => {
              setShowDeleteConfirm(false);
              setTemplateToDelete(null);
            }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={deleteTemplate}>
              <Trash size={14} className="me-1" />
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Preview Modal */}
        <Modal 
          show={showPreview} 
          onHide={() => setShowPreview(false)} 
          centered 
          size="lg"
          className={`preview-modal ${isDarkMode ? "bg-dark" : "light"}`}
          data-bs-theme={isDarkMode ? "dark" : "light"}
          style={{ padding: '20px' }}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>Email Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""} style={{ padding: '10px' }}>
            <div className="mb-3">
              <strong>To:</strong>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {emails.map((email, idx) => (
                  <Badge key={idx} bg="primary" className="me-1">
                    {email}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="mb-3">
              <strong>Subject:</strong>
              <div className="p-2 bg-light bg-opacity-10 rounded mt-1">
                {previewContent.subject}
              </div>
            </div>
            
            <div className="mb-3">
              <strong>Message Body:</strong>
              <div className="p-3 border rounded mt-1" style={{ whiteSpace: "pre-wrap", backgroundColor: isDarkMode ? "#2b2b2b" : "#f8f9fa", margin: '5px 0' }}>
                {previewContent.body}
              </div>
            </div>
            
            {link && (
              <div className="mt-3 p-3 rounded" style={{ backgroundColor: isDarkMode ? "#1e3a5f" : "#e3f2fd", borderLeft: `4px solid ${isDarkMode ? "#64b5f6" : "#1976d2"}`, margin: '10px 0' }}>
                <strong className="d-block mb-2">📥 Scanner Download Link:</strong>
                <div className="d-flex align-items-center gap-2">
                  <code style={{ wordBreak: "break-all", flex: 1 }}>{window.location.origin}{link}</code>
                  <Button size="sm" variant="outline-primary" onClick={copyLink}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
                <div className="text-center mt-2">
                  <QRCodeCanvas value={`${window.location.origin}${link}`} size={80} />
                </div>
                <small className="text-muted d-block mt-2">
                  ⚠️ {isUnlimitedDownloads ? 'Unlimited downloads' : `Can be used ${downloadLimit} times`} • {isNeverExpires ? 'Never expires' : `Expires in ${expiresIn} hours`}
                </small>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            <Button 
              variant="danger" 
              onClick={() => {
                setShowPreview(false);
                openEmailClient('gmail');
              }}
              disabled={!emails.length}
            >
              Send via Gmail
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                setShowPreview(false);
                openEmailClient('outlook');
              }}
              disabled={!emails.length}
            >
              Send via Outlook.com
            </Button>
            <Button 
              variant="info" 
              onClick={() => {
                setShowPreview(false);
                openEmailClient('outlook-office');
              }}
              disabled={!emails.length}
            >
              Send via Microsoft 365
            </Button>
          </Modal.Footer>
        </Modal>
      </Card>

      {/* Scanner Links Table */}
      {!orgid && (<Card className={`shadow-lg border-0 ${themeClass} mb-4`}>
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-4 d-flex justify-content-between align-items-center">
            <span>Generated Scanner</span>
            <Badge bg="primary">{totalCount} total</Badge>
          </h5>

          {!orgid && generatedLinks.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <Download size={40} className="mb-2" />
              <p>No scanner links generated yet.</p>
            </div>
          ) : (
            <div className="table-responsive" data-bs-theme={isDarkMode ? "dark" : "light"}>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Downloads</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!orgid && generatedLinks.map((link, idx) => {
                    const isExpired = new Date(link.expires_at) < new Date();
                    return (
                      <tr key={idx}>
                        <td>{link.org_name}</td>
                        <td>{link.download_count}/{link.download_limit === 999999 ? '∞' : link.download_limit}</td>
                        <td>
                          {link.is_revoked ? (
                            <Badge bg="danger">Revoked</Badge>
                          ) : isExpired ? (
                            <Badge bg="warning">Expired</Badge>
                          ) : link.download_count > 0 ? (
                            <Badge bg="info">Downloaded</Badge>
                          ) : (
                            <Badge bg="success">Active</Badge>
                          )}
                        </td>
                        <td>{link.download_limit === 999999 ? 'Never' : new Date(link.expires_at).toLocaleString()}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline-primary"
                              onClick={() => {
                                const fullUrl = `${window.location.origin}/download/${link.token}`;
                                navigator.clipboard.writeText(fullUrl);
                              }}
                            >
                              <Copy size={14} />
                            </Button>
                            {!link.is_revoked && !isExpired && (
                              <Button 
                                size="sm" 
                                variant="outline-danger"
                                onClick={async () => {
                                  if (window.confirm('Revoke this link?')) {
                                    try {
                                      await apifetch(`/s2/api/scanner/revoke/${link.token}`, {
                                        method: "DELETE",
                                        headers: {
                                          "Content-Type": "application/json",
                                          Authorization: `Bearer ${token}`,
                                        },
                                      });
                                      await fetchExistingLinks();
                                    } catch (err) {
                                      console.error('Failed to revoke:', err);
                                    }
                                  }
                                }}
                              >
                                <X size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>)}
    </>
  );
}